import { useTheme } from 'styled-components';

import { Typography } from '@libs/components';
import {
  formatAmountWithSymbol,
  formatBalance,
  getAmountPrefixAndColor,
} from '@libs/helpers';
import { HistoryPeriodEnumKeys } from '@libs/types';

import { CurrencyType } from 'src/constants';
import { WalletItemChangeDetails } from 'src/gql';
import { useTranslation } from 'src/hooks';

import {
  BottomSection,
  ChartChild,
  ContentContainer,
  Image,
  MidSection,
  TopSection,
} from './styles';

export const historyPeriodLabels: Partial<
  Record<HistoryPeriodEnumKeys, string>
> = {
  [HistoryPeriodEnumKeys.ONE_DAY]: 'time_24_hours',
  [HistoryPeriodEnumKeys.ONE_WEEK]: 'time_one_week_short',
  [HistoryPeriodEnumKeys.ONE_MONTH]: 'time_one_month_short',
  [HistoryPeriodEnumKeys.SIX_MONTHS]: 'time_six_month_short',
  [HistoryPeriodEnumKeys.ONE_YEAR]: 'time_one_year_short',
  [HistoryPeriodEnumKeys.CURRENT_YEAR]: 'time_one_year_short',
};

type PieChartDetailsProps = {
  totalAssets?: number;
  activeDurationKey: HistoryPeriodEnumKeys;
  currencySymbol: string;
  selectedAsset?: {
    name: string;
    amount: number;
    iconUrl: string;
    code: CurrencyType;
    value: number;
    changeDetails: WalletItemChangeDetails;
    roundingPrecision?: number;
  };
};
const PieChartDetails: React.FC<PieChartDetailsProps> = ({
  totalAssets,
  selectedAsset,
  currencySymbol,
  activeDurationKey,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  if (!selectedAsset) {
    return (
      <ChartChild>
        <Typography
          fontWeight={400}
          type="subHeadline"
          fontFamily="primaryRegular"
          color={colors.text.secondary}
        >
          {t('wallet_total')}
        </Typography>
        <Typography type="titleTwo" fontFamily="primaryBold">
          {totalAssets || 0} {t('common_asset')}
        </Typography>
      </ChartChild>
    );
  }

  const changeAmount = selectedAsset.changeDetails.valueChangeAmount;
  const changePercentage = selectedAsset.changeDetails.valueChangePercentage;
  const periodLabel = historyPeriodLabels[activeDurationKey];

  const { prefix, changeColor, absoluteAmount } = getAmountPrefixAndColor(
    colors,
    changeAmount
  );

  const formattedAmount =
    typeof selectedAsset.amount == 'number'
      ? formatAmountWithSymbol({
          symbol: selectedAsset.code,
          roundingPrecision: selectedAsset?.roundingPrecision,
          amount: selectedAsset.amount,
          shouldHaveSpace: true,
        })
      : '';

  return (
    <ContentContainer>
      <TopSection>
        <Image src={selectedAsset.iconUrl} />
        <Typography
          fontWeight={500}
          type="bodyButton"
          fontFamily="primaryMedium"
          color={colors.text.secondary}
        >
          {selectedAsset.name}
        </Typography>
      </TopSection>
      <MidSection>
        <Typography
          fontWeight={500}
          type="bodyButton"
          fontFamily="primaryMedium"
          color={colors.text.secondary}
        >
          {formattedAmount}
        </Typography>
        <Typography type="titleTwo" fontWeight={500} fontFamily="primaryMedium">
          {formatBalance(selectedAsset.value, undefined, 2)}
          {`${currencySymbol}`}
        </Typography>
      </MidSection>
      <BottomSection>
        <Typography
          type="caption"
          fontWeight={500}
          fontFamily="primaryMedium"
          color={colors.text.secondary}
        >
          {t(periodLabel)}
        </Typography>
        <Typography color={changeColor} type="caption">
          {changePercentage}% {prefix}
          {formatBalance(absoluteAmount, undefined, 2)}
        </Typography>
      </BottomSection>
    </ContentContainer>
  );
};
export default PieChartDetails;
